import React, { useState, useRef, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  XMarkIcon,
  PaperClipIcon,
  PhotoIcon,
  VideoCameraIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Document, DocumentAttachment } from '../../types/documents';
import { useToast } from '../UI/ToastProvider';
import { PublicationExpiresAtModal } from './modals/PublicationExpiresAtModal';

// Estilos CSS para o ReactQuill
const quillStyles = `
  .ql-editor {
    min-height: 300px;
    max-height: 400px;
    overflow-y: auto;
  }
  .ql-container {
    height: auto;
  }
  .ql-toolbar {
    border-top: 1px solid #ccc;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
  }
  .ql-container {
    border-bottom: 1px solid #ccc;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
  }
`;

interface DocumentEditorProps {
  document?: Document;
  onSave: (document: Document) => void;
  onPublish?: (document: Document) => void;
  /** Quando false, oculta Publicar (ex.: edição de documento com workflow). Criação deve permanecer true. */
  allowDirectPublish?: boolean;
  isAdmin?: boolean;
  onDiscard: () => void;
  onClose: () => void;
  isSaving?: boolean;
  isPublishing?: boolean;
}

const MAX_FILE_SIZE = 40 * 1024 * 1024; // 40MB

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  document,
  onSave,
  onPublish,
  allowDirectPublish = true,
  isAdmin = false,
  onDiscard,
  onClose,
  isSaving = false,
  isPublishing = false,
}) => {
  const [title, setTitle] = useState(document?.title || '');
  const [description, setDescription] = useState(document?.description || '');
  const [content, setContent] = useState(document?.content || '');
  const [attachments, setAttachments] = useState<DocumentAttachment[]>(document?.attachments || []);
  const [coverImage, setCoverImage] = useState<string | undefined>(undefined);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverImageInputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useToast();
  const [useAlternativeEditor, setUseAlternativeEditor] = useState(false);
  const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);

  // Inicializar capa e anexos quando o documento é carregado
  useEffect(() => {
    if (document) {
      setTitle(document.title || '');
      setDescription(document.description || '');
      setContent(document.content || '');
      
      // Inicializar anexos se existirem
      if (document.attachments && document.attachments.length > 0) {
        setAttachments(document.attachments);
      } else {
        setAttachments([]);
      }
      
      // Inicializar a capa se existir
      if (document.coverImage) {
        setCoverImage(document.coverImage);
      } else {
        setCoverImage(undefined);
      }
    }
  }, [document]);

  // Função para alternar entre editores
  const toggleEditor = () => {
    setUseAlternativeEditor(!useAlternativeEditor);
  };

  // Função de validação em tempo real
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    if (!title.trim()) {
      newErrors.title = 'O título é obrigatório';
    } else if (title.length > 200) {
      newErrors.title = 'O título não pode exceder 200 caracteres';
    }

    if (!description.trim()) {
      newErrors.description = 'A descrição é obrigatória';
    } else if (description.length > 400) {
      newErrors.description = 'a descrição não pode exceder 400 caracteres';
    }
    
    if (!content.trim()) {
      newErrors.content = 'O conteúdo é obrigatório';
    }
    
    // Tornar descrição opcional para evitar problemas de validação
    if (description && description.length > 400) {
      newErrors.description = 'A descrição não pode exceder 400 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Função para limpar erros quando o usuário digita
  const clearError = (field?: string) => {
    if (field) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    } else {
      setErrors({});
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newAttachments: DocumentAttachment[] = [];
    let hasError = false;

    Array.from(files).forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        addToast(`O arquivo ${file.name} excede o tamanho máximo permitido de 40MB`, 'error');
        hasError = true;
        return;
      }

      const attachment: DocumentAttachment = {
        id: Date.now().toString(),
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file),
        uploadedAt: new Date(),
        uploadedBy: 'Usuário Atual',
      };
      newAttachments.push(attachment);
    });

    if (!hasError) {
      setAttachments((prev) => [...prev, ...newAttachments]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCoverImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      addToast('A imagem de capa não pode exceder 40MB', 'error');
      return;
    }

    if (!file.type.startsWith('image/')) {
      addToast('Por favor, selecione uma imagem válida', 'error');
      return;
    }

    setCoverImage(URL.createObjectURL(file));

    if (coverImageInputRef.current) {
      coverImageInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== id));
  };

  const handleSave = async () => {
    // Validação dos campos obrigatórios
    const isValid = validateForm();
    
    if (!isValid) {
      return;
    }

    try {
      const updatedDocument: Document = {
        id: document?.id || '',
        title,
        description,
        content,
        coverImage,
        type: document?.type || 'document',
        status: 'edit', // Sempre salva como edit
        author: document?.author || 0,
        team: document?.team || 0, // Assuming team is a number or 0 if not available
        category: document?.category || 0, // Assuming category is a number or 0 if not available
        tags: document?.tags || [], // Assuming tags is an array of strings or numbers
        attachments,
        createdAt: document?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: document?.version ?? 1.0,
        publicationExpiresAt: document?.publicationExpiresAt ?? null,
        isPublic: document?.isPublic || false
      };

      onSave(updatedDocument);
    } catch (error) {
      console.error('Erro ao salvar o documento:', error);
      addToast('Erro ao salvar o documento', 'error');
    }
  };

  const handlePublish = async () => {
    // Validação dos campos obrigatórios
    const isValid = validateForm();
    
    if (!isValid) {
      return;
    }

    if (!onPublish) {
      console.error('Função onPublish não fornecida');
      return;
    }

    setShowPublishConfirmation(true);
  };

  const confirmPublish = (publicationExpiresAt: string | null) => {
    if (!onPublish) {
      console.error('Função onPublish não fornecida');
      return;
    }

    try {
      const updatedDocument: Document = {
        id: document?.id || '',
        title,
        description,
        content,
        coverImage,
        type: document?.type || 'document',
        status: 'published',
        author: document?.author || 0,
        team: document?.team || 0,
        category: document?.category || 0,
        tags: document?.tags || [],
        attachments,
        createdAt: document?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        publishedAt: new Date().toISOString(),
        publicationExpiresAt,
        version: document?.version ?? 1.0,
        isPublic: document?.isPublic || false
      };

      setShowPublishConfirmation(false);
      onPublish(updatedDocument);
    } catch (error) {
      console.error('Erro ao publicar o documento:', error);
      addToast('Erro ao publicar o documento', 'error');
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link', 'image', 'video'],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      ['clean'],
    ],
  };

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'indent',
    'align',
    'link',
    'image',
    'video',
    'color',
    'background',
    'font',
  ];

  const canShowPublishButton =
    allowDirectPublish
    && document?.status !== 'published'
    && !!onPublish
    && (!!document?.id || isAdmin);

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <style>{quillStyles}</style>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-6">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 flex items-center">
                    Editor de Documento
                    {isSaving && (
                      <div className="ml-3 flex items-center text-blue-600">
                        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm">Salvando...</span>
                      </div>
                    )}
                  </Dialog.Title>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={toggleEditor}
                      disabled={isSaving}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Alternar editor"
                    >
                      {useAlternativeEditor ? 'Editor Rico' : 'Editor Simples'}
                    </button>
                    <button
                      onClick={onClose}
                      disabled={isSaving}
                      className="text-gray-400 hover:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Capa do documento */}
                  <div className="relative h-48 bg-gray-100 rounded-lg overflow-hidden">
                    {coverImage ? (
                      <>
                        <img
                          src={coverImage}
                          alt="Capa do documento"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('=== DEBUG: Erro ao carregar imagem de capa ===', coverImage);
                            console.error('Erro:', e);
                            // Em caso de erro, remover a capa
                            setCoverImage(undefined);
                          }}
                          onLoad={() => {
                            // Imagem de capa carregada com sucesso
                          }}
                        />
                        <button
                          onClick={() => setCoverImage(undefined)}
                          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <button
                          onClick={() => coverImageInputRef.current?.click()}
                          disabled={isSaving}
                          className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <PhotoIcon className="h-5 w-5 mr-2" />
                          Adicionar Capa
                        </button>
                        <input
                          type="file"
                          ref={coverImageInputRef}
                          onChange={handleCoverImageSelect}
                          accept="image/*"
                          className="hidden"
                        />
                      </div>
                    )}
                  </div>

                  {/* Título do documento */}
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                      Título *
                    </label>
                    <input
                      type="text"
                      id="title"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        clearError('title');
                      }}
                      placeholder="Título do documento"
                      maxLength={200}
                      disabled={isSaving}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                        errors.title ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {errors.title && (
                        <p className="text-sm text-red-600">{errors.title}</p>
                      )}
                      <p className={`text-xs ${
                        title.length > 200 ? 'text-red-600' : 
                        title.length > 180 ? 'text-orange-600' : 
                        title.length > 150 ? 'text-yellow-600' : 
                        'text-gray-500'
                      }`}>
                        {title.length}/200 caracteres
                      </p>
                    </div>
                  </div>

                  {/* Descrição do documento */}
                    <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Descrição *
                      </label>
                    <textarea
                      id="description"
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                        clearError('description');
                      }}
                      placeholder="Descreva o conteúdo do documento..."
                      rows={3}
                      maxLength={400}
                      disabled={isSaving}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                          errors.description ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    <div className="flex justify-between items-center mt-1">
                      {errors.description && (
                        <p className="text-sm text-red-600">{errors.description}</p>
                      )}
                      <p className={`text-xs ${
                        description.length > 400 ? 'text-red-600' : 
                        description.length > 360 ? 'text-orange-600' : 
                        description.length > 300 ? 'text-yellow-600' : 
                        'text-gray-500'
                      }`}>
                        {description.length}/400 caracteres
                      </p>
                    </div>
                  </div>

                  {/* Editor de texto */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Conteúdo *
                    </label>
                    <div className={`border rounded-lg ${errors.content ? 'border-red-500' : 'border-gray-300'}`}>
                      {useAlternativeEditor ? (
                        <textarea
                          value={content}
                          onChange={(e) => {
                            setContent(e.target.value);
                            clearError('content');
                          }}
                          placeholder="Digite o conteúdo do documento..."
                          disabled={isSaving}
                          className="w-full h-96 p-4 border-0 resize-none focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ minHeight: '24rem' }}
                        />
                      ) : (
                        <div className="border rounded-lg">
                          <ReactQuill
                            value={content}
                            onChange={(value) => {
                              setContent(value);
                              clearError('content');
                            }}
                            modules={modules}
                            formats={formats}
                            placeholder="Digite o conteúdo do documento..."
                            readOnly={isSaving}
                          />
                        </div>
                      )}
                    </div>
                    {errors.content && (
                      <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                    )}
                  </div>

                  {/* Anexos */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-900">Anexos</h4>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isSaving}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <PaperClipIcon className="h-4 w-4 mr-1.5" />
                          Adicionar Arquivo
                        </button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          multiple
                          className="hidden"
                        />
                      </div>
                    </div>
                    {attachments.length > 0 ? (
                      <div className="space-y-2">
                        {attachments.map((attachment) => (
                          <div
                            key={attachment.id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                          >
                            <div className="flex items-center">
                              {attachment.type.startsWith('image/') ? (
                                <PhotoIcon className="h-4 w-4 text-gray-400 mr-2" />
                              ) : attachment.type.startsWith('video/') ? (
                                <VideoCameraIcon className="h-4 w-4 text-gray-400 mr-2" />
                              ) : (
                                <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                              )}
                              <span className="text-sm text-gray-900">{attachment.name}</span>
                              <span className="ml-2 text-xs text-gray-500">
                                {(attachment.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                            <button
                              onClick={() => handleRemoveAttachment(attachment.id)}
                              disabled={isSaving}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Nenhum arquivo anexado</p>
                    )}
                  </div>

                  {/* Botões de ação */}
                  <div className="flex justify-between items-center pt-4 border-t">
                    <div />
                    <div className="flex space-x-3">
                    <button
                      onClick={onDiscard}
                      disabled={isSaving || isPublishing}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Descartar
                    </button>
                    
                    {/* Botão Salvar - sempre visível */}
                    <button
                      onClick={() => {
                        handleSave();
                      }}
                      disabled={isSaving || isPublishing}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {isSaving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Salvando...
                        </>
                      ) : (
                        'Salvar'
                      )}
                    </button>

                    {/* Botão Publicar - visível apenas quando o documento não estiver publicado */}
                    {canShowPublishButton && (
                      <button
                        onClick={() => {
                          handlePublish();
                        }}
                        disabled={isSaving || isPublishing}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {isPublishing ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Publicando...
                          </>
                        ) : (
                          'Publicar'
                        )}
                      </button>
                    )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        <PublicationExpiresAtModal
          open={showPublishConfirmation}
          onClose={() => setShowPublishConfirmation(false)}
          onConfirm={confirmPublish}
          initialValue={document?.publicationExpiresAt}
          isConfirming={isPublishing}
        />
      </Dialog>
    </Transition>
  );
};

export default DocumentEditor; 